import React, { useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ThreeDot } from "react-loading-indicators";
import AdminRequestedLeave from "./AdminRequestedLeave";
import AdminSidebar from "./AdminSidebar";
import AdminAttendance from "./AdminAttendance";
import AdminHolidayList from "./AdminHolidayList";
import "bootstrap/dist/css/bootstrap.min.css";
import bgImg from "../assets/lighting.jpg";
import Employeetracker from "./Employeetracker";
import LeaveBalancePanel from "./LeaveBalancePanel";
import AdminAnnouncements from "./AdminAnnouncements";
import "./AdminDashboard.css";
import { FaTimes } from "react-icons/fa";

const UPLOAD_BASE = "https://hrms.urbango.co.in/server/uploads/employees";

function buildCandidates(employeeNo) {
  const id = String(employeeNo || "").trim();
  return [
    `${UPLOAD_BASE}/${id}.jpg`,
    `${UPLOAD_BASE}/${id}.png`,
    `${UPLOAD_BASE}/${id}.webp`,
    "https://via.placeholder.com/280x210?text=Profile",
  ];
}

function ProgressiveImg({ employeeNo, className, alt = "Profile", style }) {
  const [idx, setIdx] = React.useState(0);
  const candidates = buildCandidates(employeeNo);
  const src = candidates[Math.min(idx, candidates.length - 1)];
  return (
    <img
      className={className}
      src={src}
      alt={alt}
      style={style}
      onError={() => setIdx((i) => i + 1)}
    />
  );
}

const uploadPhoto = async (file, employeeNo) => {
  if (!file) return "";
  const fd = new FormData();
  fd.append("employeeNo", employeeNo);
  fd.append("image", file);
  const res = await fetch(
    "https://hrms.urbango.co.in/server/index.php?endpoint=upload_profile_image",
    { method: "POST", body: fd }
  );
  const data = await res.json();
  if (!data.success) {
    throw new Error(data.error || "Upload failed");
  }
  return data.url;
};

const personalFields = [
  { name: "Gender", label: "Gender" },
  { name: "DOB", label: "Date of Birth" },
  { name: "DOJ", label: "Date of Joining" },
  { name: "bloodGroup", label: "Blood Group" },
];
const idFields = [
  { name: "aadharNo", label: "Aadhar No" },
  { name: "panCard", label: "PAN" },
  { name: "passportNo", label: "Passport No" },
  { name: "UAN", label: "UAN" },
  { name: "insuranceNo", label: "Insurance No" },
  { name: "EmployeeType", label: "Employeetype" },
];
const bankFields = [
  { name: "bankName", label: "Bank Name" },
  { name: "accountNo", label: "Account No" },
  { name: "ifsc", label: "IFSC" },
];
const contactFields = [
  { name: "officialEmail", label: "Official Email" },
  { name: "phoneNumber", label: "Phone" },
];
const workFields = [
  { name: "role", label: "Role" },
  { name: "department", label: "Department" },
  { name: "reporting", label: "Reporting To" },
];

function AdminDashboard() {
  const admin = (() => {
    try {
      return JSON.parse(localStorage.getItem("admin") || "{}");
    } catch {
      return {};
    }
  })();
  const rowVariants = {
  hidden: {
    opacity: 0,
    y: 25
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.35,
      ease: "easeOut"
    }
  },
  exit: {
    opacity: 0,
    y: -15
  }
};

  // UI state
  const [showMenu, setShowMenu] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [viewingEmp, setViewingEmp] = useState(null);
  const [isClosing, setIsClosing] = useState(false);

  // Loading state
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [employeeLoading, setEmployeeLoading] = useState(true);

  const [activePage, setActivePage] = useState(
    () => localStorage.getItem("adminActiveTab") || "dashboard"
  );
  useEffect(() => {
    localStorage.setItem("adminActiveTab", activePage);
  }, [activePage]);
  const [deptFilter, setDeptFilter] = useState("");
  const [locFilter, setLocFilter] = useState("");

  const [form, setForm] = useState({
    employeeNo: "",
    username: "",
    password: "",
    employeeName: "",
    bloodGroup: "",
    officialEmail: "",
    phoneNumber: "",
    address: "",
    role: "",
    reporting: "",
    department: "",
    Gender: "",
    DOB: "",
    DOJ: "",
    aadharNo: "",
    panCard: "",
    bankName: "",
    accountNo: "",
    ifsc: "",
    passportNo: "",
    UAN: "",
    insuranceNo: "",
    nationality: "",
    maritalStatus: "",
    PermanentAddress: "",
    PermanentAddressPincode: "",
    PermanentCity: "",
    State: "",
    CurrentAddress: "",
    CurrentAddressPINCode: "",
    CurrentCity: "",
    CurrentState: "",
    FatherName: "",
    MotherName: "",
    SpouseName: "",
    EmergencyNo1: "",
    EmergencyNo2: "",
    oldUsername: "",
    photoFile: null,
    photoPreviewUrl: "",
    imageUrl: "",
    EmployeeType: "",
    orgId: admin.orgId,
  });

  const makeNextEmpNo = (list) => {
    const maxNo = (list || []).reduce((max, emp) => {
      const n = parseInt(String(emp.employeeNo || "").replace(/[^\d]/g, ""), 10) || 0;
      return n > max ? n : max;
    }, 0);
    return "emp" + String(maxNo + 1).padStart(3, "0");
  };
  const normalizeEmpNo = (raw) => {
    if (!raw) return "";
    const text = String(raw).trim();
    const m = text.match(/^emp(\d+)$/i);
    if (m) return "emp" + String(parseInt(m[1], 10)).padStart(3, "0");
    if (/^\d+$/.test(text)) return "emp" + String(parseInt(text, 10)).padStart(3, "0");
    return text;
  };

  // --- Loading Employees & Loader Animation ---
  useEffect(() => {
    setDashboardLoading(true);
    // Smooth loader delay
    setTimeout(() => setDashboardLoading(false), 480);
  }, []);

  useEffect(() => {
    let mounted = true;
    setEmployeeLoading(true);
    const adminStored = JSON.parse(localStorage.getItem("admin") || "{}");
    fetch(`https://hrms.urbango.co.in/server/index.php?endpoint=employees&orgId=${adminStored.orgId}`)
      .then(res => res.json())
      .then(data => {
        if (mounted) setEmployees(data);
        setEmployeeLoading(false);
      })
      .catch(() => { if (mounted) setEmployees([]); setEmployeeLoading(false); });
    return () => { mounted = false; };
  }, []);

  const departmentOptions = useMemo(() => {
    const s = new Set();
    for (const e of employees) {
      if (e.department && String(e.department).trim()) s.add(String(e.department).trim());
    }
    return Array.from(s).sort();
  }, [employees]);
  const locationOptions = useMemo(() => {
    const s = new Set();
    for (const e of employees) {
      if (e.address && String(e.address).trim()) s.add(String(e.address).trim());
    }
    return Array.from(s).sort();
  }, [employees]);
  const filteredEmployees = useMemo(() => {
    return employees.filter((e) => {
      const passDept = !deptFilter || (e.department && String(e.department).trim() === deptFilter);
      const passLoc = !locFilter || (e.address && String(e.address).trim() === locFilter);
      return passDept && passLoc;
    });
  }, [employees, deptFilter, locFilter]);

  const openAddForm = () => {
    const newNo = makeNextEmpNo(employees);
    setForm((f) => ({
      ...f,
      employeeNo: newNo,
      username: "",
      password: "",
      employeeName: "",
      bloodGroup: "",
      role: "",
      reporting: "",
      department: "",
      officialEmail: "",
      phoneNumber: "",
      address: "",
      Gender: "",
      DOB: "",
      DOJ: "",
      aadharNo: "",
      panCard: "",
      bankName: "",
      accountNo: "",
      ifsc: "",
      passportNo: "",
      UAN: "",
      insuranceNo: "",
      nationality: "",
      maritalStatus: "",
      PermanentAddress: "",
      PermanentAddressPincode: "",
      PermanentCity: "",
      State: "",
      CurrentAddress: "",
      CurrentAddressPINCode: "",
      CurrentCity: "",
      CurrentState: "",
      FatherName: "",
      MotherName: "",
      SpouseName: "",
      EmergencyNo1: "",
      EmergencyNo2: "",
      oldUsername: "",
      photoFile: null,
      photoPreviewUrl: "",
      EmployeeType: "",
      imageUrl: "",
      orgId: "",
    }));
    setEditIndex(null);
    setShowAddForm(true);
  };

  const openEditForm = (idx) => {
    const emp = filteredEmployees[idx];
    if (!emp) return alert("Employee not found for edit");
    fetch(
      `https://hrms.urbango.co.in/server/index.php?endpoint=get_employee_password&username=${encodeURIComponent(
        emp.username
      )}`
    )
      .then((res) => res.json())
      .then((data) => {
        setForm({
          employeeNo: emp.employeeNo,
          username: emp.username,
          password: data?.password || "",
          employeeName: emp.employeeName || "",
          bloodGroup: emp.bloodGroup || "",
          role: emp.role || "",
          reporting: emp.reporting || "",
          department: emp.department || "",
          officialEmail: emp.officialEmail || "",
          phoneNumber: emp.phoneNumber || "",
          address: emp.address || "",
          Gender: emp.Gender || "",
          DOB: emp.DOB || "",
          DOJ: emp.DOJ || "",
          aadharNo: emp.aadharNo || "",
          panCard: emp.panCard || "",
          bankName: emp.bankName || "",
          accountNo: emp.accountNo || "",
          ifsc: emp.ifsc || "",
          passportNo: emp.passportNo || "",
          UAN: emp.UAN || "",
          insuranceNo: emp.insuranceNo || "",
          nationality: emp.nationality || "",
          maritalStatus: emp.maritalStatus || "",
          PermanentAddress: emp.PermanentAddress || "",
          PermanentAddressPincode: emp.PermanentAddressPincode || "",
          PermanentCity: emp.PermanentCity || "",
          State: emp.State || "",
          CurrentAddress: emp.CurrentAddress || "",
          CurrentAddressPINCode: emp.CurrentAddressPINCode || "",
          CurrentCity: emp.CurrentCity || emp.CurremtCity || "",
          CurrentState: emp.CurrentState || "",
          FatherName: emp.FatherName || "",
          MotherName: emp.MotherName || "",
          SpouseName: emp.SpouseName || "",
          EmergencyNo1: emp.EmergencyNo1 || "",
          EmergencyNo2: emp.EmergencyNo2 || "",
          EmployeeType: emp.EmployeeType || "",
          oldUsername: emp.username || "",
          photoFile: null,
          photoPreviewUrl: "",
          imageUrl: emp.imageUrl || "",
          orgId: emp.orgId || "",
        });
        setEditIndex(idx);
        setShowAddForm(true);
      })
      .catch(() => {
        alert("Failed to load employee for editing");
      });
  };

  const handleDelete = (emp) => {
    if (!emp) return;
    if (!window.confirm("Confirm delete this employee?")) return;
    fetch("https://hrms.urbango.co.in/server/index.php?endpoint=delete_employee", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ employeeNo: emp.employeeNo, username: emp.username }),
    })
      .then((res) => res.json())
      .then((result) => {
        if (result.success) {
          return fetch(`https://hrms.urbango.co.in/server/index.php?endpoint=employees&orgId=${admin.orgId}`)
            .then((r) => r.json())
            .then((data) => setEmployees(Array.isArray(data) ? data : []));
        } else {
          alert(result.message || "Delete failed");
        }
      })
      .catch(() => alert("Delete request failed"));
  };

  const handleFormChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "employeeNo") {
      setForm((f) => ({ ...f, employeeNo: normalizeEmpNo(value) }));
      return;
    }
    if (name === "photoFile") {
      const file = files && files[0] ? files[0] : null;
      setForm((f) => ({ ...f, photoFile: file }));
      if (file) {
        const url = URL.createObjectURL(file);
        setForm((f) => ({ ...f, photoPreviewUrl: url }));
      } else {
        setForm((f) => ({ ...f, photoPreviewUrl: "" }));
      }
      return;
    }
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    const empNo = normalizeEmpNo(form.employeeNo);
    if (!empNo) return alert("Employee No is required");
    const duplicate = employees.some(
      (emp, idx) =>
        String(emp.employeeNo || "").toLowerCase() === empNo.toLowerCase() &&
        (editIndex === null ||
          String(filteredEmployees[editIndex]?.employeeNo || "").toLowerCase() !== empNo.toLowerCase())
    );
    if (duplicate) return alert("Employee No already exists. Change it or click Regenerate.");

    try {
      let imageUrl = form.imageUrl || "";
      if (form.photoFile) {
        imageUrl = await uploadPhoto(form.photoFile, empNo);
        setForm((f) => ({ ...f, imageUrl }));
      }
      const payload = {
        ...form,
        employeeNo: empNo,
        orgId: admin.orgId,
        photoFile: undefined,
        photoPreviewUrl: undefined,
        imageUrl,
      };
      const endpoint = editIndex === null ? "add_employee" : "edit_employee";
      const resp = await fetch(`https://hrms.urbango.co.in/server/index.php?endpoint=${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const result = await resp.json();
      if (result.success) {
        const list = await fetch(`https://hrms.urbango.co.in/server/index.php?endpoint=employees&orgId=${admin.orgId}`)
          .then((r) => r.json());
        setEmployees(Array.isArray(list) ? list : []);
        setShowAddForm(false);
      } else {
        alert(result.message || "Action failed");
      }
    } catch (err) {
      alert(err.message || "Unexpected error");
    }
  };

  const openViewer = (emp) => {
    setViewingEmp(emp);
    setIsClosing(false);
  };
  const closeViewer = () => {
    setIsClosing(true);
    setTimeout(() => {
      setViewingEmp(null);
      setIsClosing(false);
    }, 400); // match the close animation duration in CSS
  };

  function renderTabContent() {
    switch (activePage) {
      case "announcements":
        return <AdminAnnouncements role="admin" />;
      case "attendance":
        return <AdminAttendance role="admin" />;
      case "holiday":
        return <AdminHolidayList role="admin" />;
      case "leavebalance":
        return <LeaveBalancePanel role="admin" />;
      case "leave":
        return <AdminRequestedLeave role="admin" />;
      case "employeetracker":
        return <Employeetracker role="admin" />;
      case "dashboard":
      default:
        // Dashboard loading state
        if (dashboardLoading)
          return (
            <div style={{
              height: "100vh",
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              backdropFilter: "blur(4px)"
            }}>
              <ThreeDot color="#ff1728" size="large" />
            </div>
          );
        return (
          <div className="full-dashboard">
            <div className="admin-header-flex" style={{ gap: 12, alignItems: "center", flexWrap: "wrap" }}>
              <h2 className="title">Welcome, {admin.organizationName}!</h2>
              <label className="leave-filter">
                <b>Department:</b>
                <select
                  className="filter-btn"
                  value={deptFilter}
                  onChange={(e) => setDeptFilter(e.target.value)}
                  style={{ marginLeft: 8 }}
                >
                  <option value="">All</option>
                  {departmentOptions.map((d) => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </label>
              <label className="leave-filter">
                <b>Location:</b>
                <select
                  className="filter-btn"
                  value={locFilter}
                  onChange={(e) => setLocFilter(e.target.value)}
                  style={{ marginLeft: 8 }}
                >
                  <option value="">All</option>
                  {locationOptions.map((l) => (
                    <option key={l} value={l}>{l}</option>
                  ))}
                </select>
              </label>
              <button className="add-btn" onClick={openAddForm}>+ Add Employee</button>
            </div>
            <div className="glass-table-container">
              {employeeLoading ? (
                <div style={{
                  minHeight: "180px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center"
                }}>
                  <ThreeDot color="#6a60fe" size="medium" />
                </div>
              ) : (
                <table className="glass-table">
                  <thead className="glass-table-head">
                    <tr>
                      <th>Name</th>
                      <th>Role</th>
                      <th>Department</th>
                      <th>Official Email</th>
                      <th>Phone</th>
                      <th>Location</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody key={filteredEmployees.length}>
                    
                  <AnimatePresence initial={true} mode="popLayout">

                    
                      {filteredEmployees.map((emp, idx) => (
                        <motion.tr
        key={emp.employeeNo || idx}
        initial={{opacity: 0, y: -20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        
        transition={{
          duration: 0.55,
          ease: "easeOut",
          delay: idx * 0.05
        }}
        whileHover={{ scale: 0.985 }}
      >
                          <td>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <ProgressiveImg
                                employeeNo={emp.employeeNo}
                                className="profile-list-img"
                                style={{ width: 38, height: 38, borderRadius: "50%", background: "#191951" }}
                                alt={emp.employeeName}
                              />
                              <div>
                                <b style={{ fontSize: "1.05em" }}>{emp.employeeName}</b>
                                <div style={{ fontSize: "0.9em", color: "#C5C5FE" }}>{emp.employeeNo}</div>
                              </div>
                            </div>
                          </td>
                          <td>{emp.role}</td>
                          <td>{emp.department}</td>
                          <td>{emp.officialEmail}</td>
                          <td>{emp.phoneNumber}</td>
                          <td>{emp.address}</td>
                          <td>
                            <div className="actions-flex">
                              <button className="view-btn" title="View" onClick={() => openViewer(emp)}>👁️</button>
                              <button className="edit-btn" onClick={() => openEditForm(idx)}>Edit</button>
                              <button className="delete-btn" onClick={() => handleDelete(emp)}>Delete</button>
                            </div>
                          </td>
                        </motion.tr>
                      ))}
                      {filteredEmployees.length === 0 && (
                        <tr>
                          <td colSpan="7">No employees match selected filters</td>
                        </tr>
                      )}
                    
                  </AnimatePresence>
                  </tbody>
                </table>
              )}
              {/* Modals below */}
              {showAddForm &&
                ReactDOM.createPortal(
                  <div className="modal-bg" role="dialog" aria-modal="true">
                    <form className="modal-form" onSubmit={handleFormSubmit}>
                      <h3>{editIndex === null ? "Add Employee" : "Edit Employee"}</h3>
                      <button type="button" aria-label="Close Sidebar" className="offcanvas-close" onClick={() => setShowAddForm(false)}>
                        <FaTimes />
                      </button>
                      <div className="row-flex">
                        <input
                          name="employeeNo"
                          value={form.employeeNo}
                          onChange={handleFormChange}
                          placeholder="Employee No (e.g., emp001)"
                          required
                        />
                        <button
                          type="button"
                          className="regen-btn"
                          onClick={() => setForm((f) => ({ ...f, employeeNo: makeNextEmpNo(employees) }))}
                          title="Regenerate next available ID"
                        >
                          Regenerate
                        </button>
                      </div>
                      <div className="photo-row">
                        <div className="photo-preview">
                          {form.photoPreviewUrl ? (
                            <img src={form.photoPreviewUrl} alt="Profile preview" />
                          ) : form.imageUrl ? (
                            <img src={form.imageUrl} alt="Existing profile" />
                          ) : (
                            <ProgressiveImg employeeNo={form.employeeNo} alt="Profile preview" />
                          )}
                        </div>
                        <div className="photo-inputs">
                          <input type="file" name="photoFile" accept="image/*" onChange={handleFormChange} />
                          <small className="hint">
                            JPG/PNG/WebP up to 2MB; uploaded to {UPLOAD_BASE}/{form.employeeNo}.ext
                          </small>
                        </div>
                      </div>
                      <div className="grid two">
                        <input name="username" placeholder="Username" value={form.username} onChange={handleFormChange} required />
                        <input name="password" type="password" placeholder="Password" value={form.password} onChange={handleFormChange} required />
                        <input name="employeeName" placeholder="Full Name" value={form.employeeName} onChange={handleFormChange} required />
                        <input name="address" placeholder="Location (City/Office)" value={form.address} onChange={handleFormChange} />
                      </div>
                      <div className="section-title">Work</div>
                      <div className="grid three">
                        {workFields.map((f) => (
                          <input key={f.name} name={f.name} placeholder={f.label} value={form[f.name] || ""} onChange={handleFormChange} />
                        ))}
                      </div>
                      <div className="section-title">Contact</div>
                      <div className="grid two">
                        {contactFields.map((f) => (
                          <input key={f.name} name={f.name} placeholder={f.label} value={form[f.name] || ""} onChange={handleFormChange} />
                        ))}
                      </div>
                      <div className="section-title">Personal</div>
                      <div className="grid four">
                        {personalFields.map((f) => (
                          <input key={f.name} name={f.name} placeholder={f.label} value={form[f.name] || ""} onChange={handleFormChange} />
                        ))}
                        <input name="nationality" placeholder="Nationality" value={form.nationality} onChange={handleFormChange} />
                        <input name="maritalStatus" placeholder="Marital Status" value={form.maritalStatus} onChange={handleFormChange} />
                      </div>
                      <div className="section-title">Identity</div>
                      <div className="grid four liquid-glass">
                        {idFields.map((f) =>
                          f.name === "EmployeeType" ? (
                            <select key={f.name} name={f.name} value={form[f.name] || ""} onChange={handleFormChange}>
                              <option value="">Select Employee Type</option>
                              <option value="Intern">Intern</option>
                              <option value="Full Time">Full Time</option>
                              <option value="Probation">Probation</option>
                            </select>
                          ) : (
                            <input key={f.name} name={f.name} placeholder={f.label} value={form[f.name] || ""} onChange={handleFormChange} />
                          )
                        )}
                      </div>
                      <div className="section-title">Bank</div>
                      <div className="grid three">
                        {bankFields.map((f) => (
                          <input key={f.name} name={f.name} placeholder={f.label} value={form[f.name] || ""} onChange={handleFormChange} />
                        ))}
                      </div>
                      <div className="section-title">Permanent Address</div>
                      <div className="grid four">
                        {[
                          { name: "PermanentAddress", label: "Permanent Address" },
                          { name: "PermanentCity", label: "Permanent City" },
                          { name: "State", label: "Permanent State" },
                          { name: "PermanentAddressPincode", label: "Permanent Pincode" },
                        ].map((f) => (
                          <input key={f.name} name={f.name} placeholder={f.label} value={form[f.name] || ""} onChange={handleFormChange} />
                        ))}
                      </div>
                      <div className="section-title">Current Address</div>
                      <div className="grid four">
                        {[
                          { name: "CurrentAddress", label: "Current Address" },
                          { name: "CurrentCity", label: "Current City" },
                          { name: "CurrentState", label: "Current State" },
                          { name: "CurrentAddressPINCode", label: "Current Pincode" },
                        ].map((f) => (
                          <input key={f.name} name={f.name} placeholder={f.label} value={form[f.name] || ""} onChange={handleFormChange} />
                        ))}
                      </div>
                      <div className="section-title">Family & Emergency</div>
                      <div className="grid four">
                        {[
                          { name: "FatherName", label: "Father's Name" },
                          { name: "MotherName", label: "Mother's Name" },
                          { name: "SpouseName", label: "Spouse's Name" },
                          { name: "EmergencyNo1", label: "Emergency No 1" },
                          { name: "EmergencyNo2", label: "Emergency No 2" },
                        ].map((f) => (
                          <input key={f.name} name={f.name} placeholder={f.label} value={form[f.name] || ""} onChange={handleFormChange} />
                        ))}
                      </div>
                      <div className="form-actions">
                        <button className="add-btn" type="submit">
                          {editIndex === null ? "Add" : "Save"}
                        </button>
                        <button className="cancel-btn" type="button" onClick={() => setShowAddForm(false)}>
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>,
                  document.body
                )}
              {viewingEmp &&
                ReactDOM.createPortal(
                  <div className={`glass-viewer-overlay${isClosing ? ' closing' : ''}`} onClick={closeViewer} role="dialog" aria-modal="true">
                    <div className="glass-viewer-card" onClick={e => e.stopPropagation()}>
                      <div className="viewer-header">
                        <h3 className="viewer-title">Employee Profile</h3>
                        <button className="viewer-close" onClick={closeViewer} aria-label="Close">✕</button>
                      </div>
                      <div className="viewer-body">
                        <div className="viewer-left">
                          {viewingEmp.imageUrl ? (
                            <img className="viewer-avatar" src={viewingEmp.imageUrl} alt={viewingEmp.employeeName} />
                          ) : (
                            <ProgressiveImg
                              className="viewer-avatar"
                              employeeNo={viewingEmp.employeeNo}
                              alt={viewingEmp.employeeName}
                            />
                          )}
                          <div className="viewer-primary">
                            <div className="viewer-name">{viewingEmp.employeeName}</div>
                            <div className="viewer-sub">{viewingEmp.role} • {viewingEmp.department}</div>
                            <div className="viewer-id">Date of Joining: {viewingEmp.DOJ}</div>
                            <div className="viewer-id">username: {viewingEmp.username} </div>
                            <div className="viewer-id">Employee No: {viewingEmp.employeeNo}</div>
                            <div className="viewer-id">Employee Type: {viewingEmp.EmployeeType}</div>
                          </div>
                        </div>
                        <div className="viewer-right">
                          <div className="viewer-tabs">
                            <button className="viewer-tab active">About</button>
                          </div>
                          <div className="viewer-divider" />
                          <div className="viewer-grid">
                            <div className="viewer-section">Contact</div>
                            <div className="viewer-kv">
                              <span className="k">Email</span>
                              <span className="v">{viewingEmp.officialEmail}</span>
                              <span className="k">Phone</span>
                              <span className="v">{viewingEmp.phoneNumber}</span>
                              <span className="k">Location</span>
                              <span className="v">{viewingEmp.address}</span>
                            </div>
                            <div className="viewer-section">Work</div>
                            <div className="viewer-kv">
                              <span className="k">Role</span>
                              <span className="v">{viewingEmp.role}</span>
                              <span className="k">Department</span>
                              <span className="v">{viewingEmp.department}</span>
                              <span className="k">Reporting To</span>
                              <span className="v">{viewingEmp.reporting}</span>
                            </div>
                            <div className="viewer-section">Personal</div>
                            <div className="viewer-kv">
                              <span className="k">Gender</span>
                              <span className="v">{viewingEmp.Gender}</span>
                              <span className="k">Blood Group</span>
                              <span className="v">{viewingEmp.bloodGroup}</span>
                              <span className="k">Date of Birth</span>
                              <span className="v">{viewingEmp.DOB}</span>
                              <span className="k">DOJ</span>
                              <span className="v">{viewingEmp.DOJ}</span>
                              <span className="k">Nationality</span>
                              <span className="v">{viewingEmp.nationality}</span>
                              <span className="k">Marital Status</span>
                              <span className="v">{viewingEmp.maritalStatus}</span>
                            </div>
                            <div className="viewer-section">Identity</div>
                            <div className="viewer-kv">
                              <span className="k">Aadhar</span>
                              <span className="v">{viewingEmp.aadharNo}</span>
                              <span className="k">PAN</span>
                              <span className="v">{viewingEmp.panCard}</span>
                              <span className="k">Passport</span>
                              <span className="v">{viewingEmp.passportNo}</span>
                              <span className="k">UAN</span>
                              <span className="v">{viewingEmp.UAN}</span>
                              <span className="k">Insurance</span>
                              <span className="v">{viewingEmp.insuranceNo}</span>
                            </div>
                            <div className="viewer-section">Bank</div>
                            <div className="viewer-kv">
                              <span className="k">Bank</span>
                              <span className="v">{viewingEmp.bankName}</span>
                              <span className="k">Account</span>
                              <span className="v">{viewingEmp.accountNo}</span>
                              <span className="k">IFSC</span>
                              <span className="v">{viewingEmp.ifsc}</span>
                            </div>
                            <div className="viewer-section">Permanent Address</div>
                            <div className="viewer-kv">
                              <span className="k">Address</span>
                              <span className="v">{viewingEmp.PermanentAddress}</span>
                              <span className="k">City</span>
                              <span className="v">{viewingEmp.PermanentCity}</span>
                              <span className="k">State</span>
                              <span className="v">{viewingEmp.State}</span>
                              <span className="k">Pincode</span>
                              <span className="v">{viewingEmp.PermanentAddressPincode}</span>
                            </div>
                            <div className="viewer-section">Current Address</div>
                            <div className="viewer-kv">
                              <span className="k">Address</span>
                              <span className="v">{viewingEmp.CurrentAddress}</span>
                              <span className="k">City</span>
                              <span className="v">{viewingEmp.CurrentCity || viewingEmp.CurremtCity}</span>
                              <span className="k">State</span>
                              <span className="v">{viewingEmp.CurrentState}</span>
                              <span className="k">Pincode</span>
                              <span className="v">{viewingEmp.CurrentAddressPINCode}</span>
                            </div>
                            <div className="viewer-section">Family & Emergency</div>
                            <div className="viewer-kv">
                              <span className="k">Father</span>
                              <span className="v">{viewingEmp.FatherName}</span>
                              <span className="k">Mother</span>
                              <span className="v">{viewingEmp.MotherName}</span>
                              <span className="k">Spouse</span>
                              <span className="v">{viewingEmp.SpouseName}</span>
                              <span className="k">Emergency 1</span>
                              <span className="v">{viewingEmp.EmergencyNo1}</span>
                              <span className="k">Emergency 2</span>
                              <span className="v">{viewingEmp.EmergencyNo2}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>,
                  document.body
                )}
            </div>
          </div>
        );
    }
  }

  return (
    <div className="dashboard-root" style={{
      backgroundImage: `url(${bgImg})`,
      backgroundSize: "cover",
      height: "100vh",
      position: "relative",
    }}>
      <div className="dashboard-layout" style={{ display: "flex", position: "relative", zIndex: 1 }}>
        <AdminSidebar admin={admin} activePage={activePage} setActivePage={setActivePage} />
        <div style={{ flex: 1 }}>{renderTabContent()}</div>
      </div>
    </div>
  );
}

export default AdminDashboard;
